// The Analytics page's charts, drawn as plain SVG so they take the admin's
// tokens and type. Thin marks, hairline grid, one hue unless a second series
// is the point; every value a hover shows is also printed somewhere on the card.

import {useLayoutEffect, useRef, useState} from 'react';
import type {KeyboardEvent, PointerEvent, ReactNode} from 'react';
import type {DayPoint, RetentionCohort, Share} from '../../../shared/analytics-api.ts';
import {Icon} from './Icon.tsx';

/* ------------------------------------------------------------------ sizing */

/** The element's width in CSS pixels, so text in the SVG stays its real size. */
function useWidth<T extends HTMLElement>(): [React.RefObject<T | null>, number] {
  const ref = useRef<T>(null);
  const [width, setWidth] = useState(0);
  useLayoutEffect(() => {
    const node = ref.current;
    if (!node) return;
    setWidth(node.clientWidth);
    const observer = new ResizeObserver(([entry]) => setWidth(Math.round(entry!.contentRect.width)));
    observer.observe(node);
    return () => observer.disconnect();
  }, []);
  return [ref, width];
}

/** A round step for the axis, so it carries five or fewer intervals. */
function axisStep(max: number): number {
  if (max <= 5) return 1;
  const power = 10 ** Math.floor(Math.log10(max / 5));
  for (const factor of [1, 2, 2.5, 5, 10]) {
    const step = factor * power;
    if (step >= 1 && Number.isInteger(step) && Math.ceil(max / step) <= 5) return step;
  }
  return 10 * power;
}

const compact = new Intl.NumberFormat('en', {notation: 'compact', maximumFractionDigits: 1});
const whole = new Intl.NumberFormat('en');

export function formatNumber(value: number): string {
  return Math.abs(value) >= 10_000 ? compact.format(value) : whole.format(Math.round(value));
}

export function dayLabel(day: string, withWeekday = false): string {
  const date = new Date(`${day}T00:00:00Z`);
  return date.toLocaleDateString('en', {timeZone: 'UTC', month: 'short', day: 'numeric', ...(withWeekday ? {weekday: 'short'} : {})});
}

/* -------------------------------------------------------------- sparkline */

/** A small trend in the de-emphasis gray, the latest day in the accent. */
export function Sparkline({points, label}: {points: DayPoint[]; label: string}) {
  if (points.length < 2) return null;
  const width = 112;
  const height = 32;
  const max = Math.max(1, ...points.map((point) => point.value));
  const x = (index: number) => 3 + (index / (points.length - 1)) * (width - 6);
  const y = (value: number) => height - 3 - (value / max) * (height - 6);
  const path = points.map((point, index) => `${index ? 'L' : 'M'}${x(index).toFixed(1)},${y(point.value).toFixed(1)}`).join('');
  const last = points[points.length - 1]!;
  return (
    <svg className="sparkline" width={width} height={height} viewBox={`0 0 ${width} ${height}`} role="img" aria-label={label}>
      <path d={path} fill="none" stroke="var(--chart-muted)" strokeWidth={1.5} strokeLinejoin="round" strokeLinecap="round" />
      <circle cx={x(points.length - 1)} cy={y(last.value)} r={3} fill="var(--chart-1)" stroke="var(--surface)" strokeWidth={1.5} />
    </svg>
  );
}

/* ------------------------------------------------------------ trend chart */

export interface TrendSeries {
  key: string;
  label: string;
  /** A CSS color, one of the --chart-* tokens. */
  color: string;
  points: DayPoint[];
  /** A comparison drawn thin and dashed behind the main series (e.g. the days before). */
  dashed?: boolean;
}

/** The legend's and the tooltip's key for a series: a short line, dashed for a comparison. */
function keyStyle(one: TrendSeries) {
  return {background: one.dashed ? `repeating-linear-gradient(90deg, ${one.color} 0 4px, transparent 4px 7px)` : one.color};
}

const PAD = {top: 12, right: 14, bottom: 26, left: 40};

/**
 * Daily values on one axis. A crosshair snaps to the nearest day and one
 * tooltip lists every series; the arrow keys move it when the chart has focus.
 */
export function TrendChart({series, height = 240, empty, format = formatNumber}: {
  series: TrendSeries[];
  height?: number;
  empty?: ReactNode;
  /** How the axis and the tooltip write a value. */
  format?: (value: number) => string;
}) {
  const [box, width] = useWidth<HTMLDivElement>();
  const [hover, setHover] = useState<number | null>(null);
  const days = series[0]?.points ?? [];
  const peak = Math.max(0, ...series.flatMap((one) => one.points.map((point) => point.value)));
  const step = axisStep(peak);
  const max = Math.max(step, Math.ceil(peak / step) * step);
  const innerW = Math.max(0, width - PAD.left - PAD.right);
  const innerH = height - PAD.top - PAD.bottom;
  const x = (index: number) => PAD.left + (days.length < 2 ? innerW / 2 : (index / (days.length - 1)) * innerW);
  const y = (value: number) => PAD.top + innerH - (value / max) * innerH;
  const ticks = Array.from({length: Math.round(max / step) + 1}, (_, index) => index * step);
  const labelEvery = Math.max(1, Math.ceil(days.length / Math.max(2, Math.floor(innerW / 76))));
  const allZero = series.every((one) => one.points.every((point) => point.value === 0));

  function pick(event: PointerEvent<SVGRectElement>) {
    const rect = event.currentTarget.getBoundingClientRect();
    const ratio = (event.clientX - rect.left) / Math.max(1, rect.width);
    setHover(Math.max(0, Math.min(days.length - 1, Math.round(ratio * (days.length - 1)))));
  }
  function key(event: KeyboardEvent<HTMLDivElement>) {
    if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
    event.preventDefault();
    setHover((current) => {
      const from = current ?? days.length - 1;
      return Math.max(0, Math.min(days.length - 1, from + (event.key === 'ArrowLeft' ? -1 : 1)));
    });
  }

  const tipLeft = hover === null ? 0 : x(hover);
  const flip = tipLeft > width - 190;
  return (
    <div className="trend">
      {series.length > 1 ? (
        <div className="chart-legend">
          {series.map((one) => <span key={one.key}><i className="key-line" style={keyStyle(one)} />{one.label}</span>)}
        </div>
      ) : null}
      <div className="trend-box" ref={box} tabIndex={0} onKeyDown={key} onBlur={() => setHover(null)}
        aria-label={`${series.map((one) => one.label).join(' and ')}, by day`}>
        {allZero && empty ? <div className="chart-empty">{empty}</div> : null}
        {width > 0 && days.length ? (
          <svg width={width} height={height} role="img" aria-hidden="true">
            {ticks.map((tick) => (
              <g key={tick}>
                <line x1={PAD.left} x2={width - PAD.right} y1={y(tick)} y2={y(tick)} stroke="var(--chart-grid)" strokeWidth={1} />
                <text x={PAD.left - 8} y={y(tick)} dy="0.32em" textAnchor="end" className="chart-tick">{format(tick)}</text>
              </g>
            ))}
            {days.map((point, index) => (index % labelEvery === 0 || index === days.length - 1) && (index === days.length - 1 || x(days.length - 1) - x(index) >= 64) ? (
              <text key={point.day} x={x(index)} y={height - 8} textAnchor={index === 0 ? 'start' : index === days.length - 1 ? 'end' : 'middle'} className="chart-tick">
                {dayLabel(point.day)}
              </text>
            ) : null)}
            {series.slice(1).every((one) => one.dashed) && !(allZero && empty) ? (
              <path
                d={`M${x(0)},${y(0)} ${days.map((point, index) => `L${x(index).toFixed(1)},${y(point.value).toFixed(1)}`).join(' ')} L${x(days.length - 1)},${y(0)} Z`}
                fill={series[0]!.color} opacity={0.1}
              />
            ) : null}
            {allZero && empty ? null : series.map((one) => (
              <path key={one.key} fill="none" stroke={one.color} strokeWidth={one.dashed ? 1.5 : 2} strokeLinejoin="round" strokeLinecap="round"
                strokeDasharray={one.dashed ? '5 4' : undefined}
                d={one.points.map((point, index) => `${index ? 'L' : 'M'}${x(index).toFixed(1)},${y(point.value).toFixed(1)}`).join('')} />
            ))}
            {hover !== null ? <line x1={x(hover)} x2={x(hover)} y1={PAD.top} y2={PAD.top + innerH} stroke="var(--chart-cross)" strokeWidth={1} /> : null}
            {allZero && empty ? null : series.map((one) => {
              const index = hover ?? one.points.length - 1;
              const point = one.points[index];
              return point ? <circle key={one.key} cx={x(index)} cy={y(point.value)} r={one.dashed ? 3 : 4} fill={one.color} stroke="var(--surface)" strokeWidth={2} /> : null;
            })}
            <rect x={PAD.left} y={0} width={innerW} height={height} fill="transparent"
              onPointerMove={pick} onPointerDown={pick} onPointerLeave={() => setHover(null)} />
          </svg>
        ) : null}
        {hover !== null && days[hover] ? (
          <div className={flip ? 'chart-tip flip' : 'chart-tip'} style={{left: tipLeft}} role="status">
            <div className="tip-head">{dayLabel(days[hover]!.day, true)}</div>
            {series.map((one) => (
              <div className="tip-row" key={one.key}>
                <i className="key-line" style={keyStyle(one)} />
                <b>{format(one.points[hover]?.value ?? 0)}</b>
                <span>{one.label}</span>
              </div>
            ))}
          </div>
        ) : null}
      </div>
    </div>
  );
}

/* ----------------------------------------------------------------- funnel */

export function formatHours(hours: number | null): string {
  if (hours === null) return '';
  if (hours < 1) return 'within an hour';
  if (hours < 36) return `${Math.round(hours)} h`;
  const daysCount = hours / 24;
  return `${daysCount < 10 ? daysCount.toFixed(1).replace(/\.0$/, '') : Math.round(daysCount)} days`;
}

/**
 * People at each step, as a share of the first step. The biggest drop is
 * marked on the step people stop at (the one before the drop), because that
 * screen or stage is where to look first.
 */
export interface FunnelRow {
  key: string;
  label: string;
  count: number;
  median_hours?: number | null;
  /** Some people skip this step by design; it is not compared with its neighbours. */
  optional?: string;
}

/** `unit` names what is counted, in the column head: people, or brands. */
export function Funnel({steps, timing = 'after sign-up', unit = 'People'}: {steps: FunnelRow[]; timing?: string; unit?: string}) {
  const base = steps[0]?.count ?? 0;
  /** The last step everyone goes through, before each step. */
  const previous = steps.map((_, index) => {
    for (let back = index - 1; back >= 0; back -= 1) if (!steps[back]!.optional) return steps[back]!.count;
    return 0;
  });
  /** The last step everyone goes through, by index, before each step. */
  const previousIndex = steps.map((_, index) => {
    for (let back = index - 1; back >= 0; back -= 1) if (!steps[back]!.optional) return back;
    return -1;
  });
  let worst = -1;
  let worstLoss = 0;
  let worstLost = 0;
  steps.forEach((step, index) => {
    if (!index || step.optional) return;
    const before = previous[index]!;
    const loss = before ? (before - step.count) / before : 0;
    if (before >= 5 && loss > worstLoss) {
      worstLoss = loss;
      worst = previousIndex[index]!;
      worstLost = before - step.count;
    }
  });
  const timed = steps.some((step) => step.median_hours !== null && step.median_hours !== undefined);
  return (
    <ol className="funnel">
      {timed ? (
        <li className="funnel-row funnel-head" aria-hidden="true">
          <span /><span /><span>{unit}</span><span /><span>{timing}</span><span />
        </li>
      ) : null}
      {steps.map((step, index) => {
        const share = base ? step.count / base : 0;
        const stops = index === worst && worstLoss >= 0.1;
        return (
          <li key={step.key} className={`funnel-row${stops ? ' worst' : ''}${step.optional ? ' optional' : ''}`}>
            <span className="funnel-label" title={step.label}>
              <span className="funnel-name">{step.label}</span>
              {step.optional ? <Tip text={step.optional} /> : null}
            </span>
            <span className="funnel-track" title={`${whole.format(step.count)} of ${whole.format(base)}`}>
              <span className="funnel-fill" style={{width: `${Math.max(step.count ? 0.6 : 0, share * 100)}%`}} />
            </span>
            <span className="funnel-count">{whole.format(step.count)}</span>
            <span className="funnel-share">{base ? `${share >= 0.995 || share === 0 ? Math.round(share * 100) : (share * 100).toFixed(share < 0.1 ? 1 : 0)}%` : ''}</span>
            <span className="funnel-time" title={step.median_hours !== null && step.median_hours !== undefined ? `Median: ${formatHours(step.median_hours)} ${timing}` : undefined}>
              {step.median_hours !== null && step.median_hours !== undefined ? shortHours(step.median_hours) : ''}
            </span>
            <span className="funnel-flag">{stops ? <em>{whole.format(worstLost)} stop here</em> : null}</span>
          </li>
        );
      })}
    </ol>
  );
}

/** A duration in a few characters, for a column: "<1 h", "30 h", "2.2 d". */
function shortHours(hours: number): string {
  if (hours < 1) return '<1 h';
  if (hours < 48) return `${Math.round(hours)} h`;
  const days = hours / 24;
  return `${days < 10 ? days.toFixed(1).replace(/\.0$/, '') : Math.round(days)} d`;
}

/** A small info mark whose note opens on hover or focus. */
export function Tip({text}: {text: string}) {
  return (
    <span className="tip" tabIndex={0} role="note" aria-label={text} data-tip={text}>
      <Icon name="info" size={13} />
    </span>
  );
}

/* ------------------------------------------------------------ share bars */

/** How answers split: the bar is the share of everyone who answered. */
export function ShareBars({items, labelOf, limit = 6, total}: {
  items: Share[];
  labelOf?: (key: string) => string;
  limit?: number;
  /** The base the shares are of; the answers' own sum when absent. */
  total?: number;
}) {
  const base = total ?? items.reduce((sum, item) => sum + item.count, 0);
  const shown = items.length > limit ? items.slice(0, limit - 1) : items;
  const rest = items.length > limit ? items.slice(limit - 1).reduce((sum, item) => sum + item.count, 0) : 0;
  const rows = rest ? [...shown, {key: '__other', count: rest}] : shown;
  const top = Math.max(1, ...rows.map((row) => row.count));
  if (!rows.length) return <p className="muted small">Nothing yet.</p>;
  return (
    <ul className="shares">
      {rows.map((row) => (
        <li key={row.key}>
          <span className="share-label">{row.key === '__other' ? `${items.length - shown.length} more` : labelOf ? labelOf(row.key) : row.key}</span>
          <span className="share-track"><span className="share-fill" style={{width: `${(row.count / top) * 100}%`}} /></span>
          <span className="share-value">{row.key === '__other' ? null : <b>{base ? `${Math.round((row.count / base) * 100)}%` : '–'}</b>}<small>{formatNumber(row.count)}</small></span>
        </li>
      ))}
    </ul>
  );
}

/* --------------------------------------------------------------- cohorts */

/** Weekly cohorts: one row per first week, the share still active in each week after. */
export function CohortTable({cohorts}: {cohorts: RetentionCohort[]}) {
  const rows = cohorts.filter((row) => row.people > 0);
  const columns = Math.max(1, ...rows.map((row) => row.active.filter((value) => value !== null).length));
  if (!rows.length) return <p className="muted small">No one yet.</p>;
  return (
    <div className="cohorts" role="table" aria-label="Weekly cohorts">
      <div className="cohort-row head" role="row">
        <span role="columnheader">First week</span>
        <span role="columnheader">People</span>
        {Array.from({length: columns}, (_, index) => <span role="columnheader" key={index}>{index === 0 ? 'Wk 0' : `+${index}`}</span>)}
      </div>
      {rows.map((row) => (
        <div className="cohort-row" role="row" key={row.week}>
          <span role="cell">{dayLabel(row.week)}</span>
          <span role="cell" className="num">{whole.format(row.people)}</span>
          {Array.from({length: columns}, (_, index) => {
            const value = row.active[index];
            if (value === null || value === undefined) return <span role="cell" key={index} className="cell none" />;
            const share = row.people ? value / row.people : 0;
            return (
              <span role="cell" key={index} className="cell" title={`${whole.format(value)} of ${whole.format(row.people)}`}
                style={{background: `color-mix(in srgb, var(--chart-1) ${Math.round(8 + share * 52)}%, var(--surface))`}}>
                {Math.round(share * 100)}%
              </span>
            );
          })}
        </div>
      ))}
    </div>
  );
}

/* ------------------------------------------------------------------ delta */

/**
 * The change against the period before, colored by whether up is good. Short,
 * since the page says once what it compares with; the full sentence is on hover.
 */
export function Delta({value, previous, upIsGood = true, days}: {value: number; previous: number; upIsGood?: boolean; days: number}) {
  const full = `${formatNumber(value)} now, ${formatNumber(previous)} in the previous ${days} days`;
  // Nothing then and nothing now is not news.
  if (value === previous) return value ? <span className="delta flat" title={full}>no change</span> : null;
  const up = value > previous;
  const tone = up === upIsGood ? 'good' : 'bad';
  const ratio = previous ? value / previous : 0;
  // Below ten a percentage overstates the change: 1 → 0 would read "-100%".
  const text = !previous ? 'none before'
    : previous < 10 || value === 0 ? `${up ? 'up' : 'down'} from ${formatNumber(previous)}`
    : ratio >= 3 ? `${ratio < 10 ? ratio.toFixed(1).replace(/\.0$/, '') : Math.round(ratio)}× before`
    : `${Math.round(Math.abs(ratio - 1) * 100)}% ${up ? 'more' : 'fewer'}`;
  return <span className={`delta ${tone}`} title={full}><Icon name={up ? 'arrowUp' : 'arrowDown'} size={13} />{text}</span>;
}
